import type { GameBridge, ToolResult } from "./GameBridge";
import {
  toJsonSchema,
  SceneResponseSchema,
  EntityDetailSchema,
  InventoryResponseSchema,
  KnowledgeResponseSchema,
  NearbyResponseSchema,
  MoveResultSchema,
  TakeResultSchema,
  DropResultSchema,
  InteractResultSchema,
  NavigateResultSchema,
  EquipResultSchema,
  UnequipResultSchema,
  DiscoverResultSchema,
  ExamineResultSchema,
  RestartResultSchema,
} from "./schemas";

export interface McpToolDef {
  readonly name: string;
  readonly title: string;
  readonly description: string;
  readonly inputSchema: Record<string, unknown>;
  readonly outputSchema: Record<string, unknown>;
  readonly execute: (
    args: Record<string, unknown>,
  ) => Promise<ToolResult> | ToolResult;
  readonly annotations?:
    | {
        readOnlyHint?: boolean;
        destructiveHint?: boolean;
        idempotentHint?: boolean;
        openWorldHint?: boolean;
      }
    | undefined;
}

export function defineTools(bridge: GameBridge): readonly McpToolDef[] {
  return [
    // ── Read-only observation tools ──────────────────────────────────
    {
      name: "get-scene",
      title: "Get Scene",
      description:
        "Returns the current scene: id, title, size, player position, all visible entities (with positions, kinds, descriptions, available actions), and portals. Use this to understand the current game state before taking actions.",
      inputSchema: { type: "object", properties: {} },
      outputSchema: toJsonSchema(SceneResponseSchema),
      execute: () => ({
        content: [{ type: "text", text: JSON.stringify(bridge.getScene()) }],
      }),
      annotations: { readOnlyHint: true, idempotentHint: true },
    },
    {
      name: "get-entity",
      title: "Get Entity",
      description:
        "Returns full details of a specific entity: position, facing, visible, description, available actions, component types. Use this to inspect an entity before interacting with it.",
      inputSchema: {
        type: "object",
        properties: {
          entityId: {
            type: "string",
            description:
              "The entity id (e.g. 'kuki' for player, 'garden-tree', 'forest-gate', 'door-back')",
          },
        },
        required: ["entityId"],
      },
      outputSchema: toJsonSchema(EntityDetailSchema),
      execute: (args) => {
        const id = args.entityId as string;
        if (!id)
          return {
            content: [{ type: "text", text: "Error: entityId is required" }],
          };
        const result = bridge.getEntity(id);
        return result
          ? { content: [{ type: "text", text: JSON.stringify(result) }] }
          : {
              content: [
                {
                  type: "text",
                  text: `Entity "${id}" not found in current scene`,
                },
              ],
            };
      },
      annotations: { readOnlyHint: true, idempotentHint: true },
    },
    {
      name: "get-inventory",
      title: "Get Inventory",
      description:
        "Returns the player's inventory: all held items with instanceId, definitionId, category, quantity. Use this to see what the player is carrying.",
      inputSchema: { type: "object", properties: {} },
      outputSchema: toJsonSchema(InventoryResponseSchema),
      execute: () => ({
        content: [
          { type: "text", text: JSON.stringify(bridge.getInventory()) },
        ],
      }),
      annotations: { readOnlyHint: true, idempotentHint: true },
    },
    {
      name: "get-knowledge",
      title: "Get Knowledge",
      description:
        "Returns knowledge graph progress: which nodes are discovered, partially understood, or understood. Use this to track game progression.",
      inputSchema: { type: "object", properties: {} },
      outputSchema: toJsonSchema(KnowledgeResponseSchema),
      execute: () => ({
        content: [
          { type: "text", text: JSON.stringify(bridge.getKnowledge()) },
        ],
      }),
      annotations: { readOnlyHint: true, idempotentHint: true },
    },
    {
      name: "get-nearby",
      title: "Get Nearby Entities",
      description:
        "Returns entities near the player within the given radius (default 300px), sorted by distance. Useful for discovering interactable objects around the player.",
      inputSchema: {
        type: "object",
        properties: {
          radius: {
            type: "number",
            description:
              "Search radius in pixels (default 300, max recommended 2000)",
          },
        },
      },
      outputSchema: toJsonSchema(NearbyResponseSchema),
      execute: (args) => {
        const radius = args.radius != null ? Number(args.radius) : undefined;
        const nearby = bridge.getNearby(radius);
        return {
          content: [
            {
              type: "text",
              text: JSON.stringify({ count: nearby.length, entities: nearby }),
            },
          ],
        };
      },
      annotations: { readOnlyHint: true, idempotentHint: true },
    },

    // ── Movement tools ──────────────────────────────────────────────
    {
      name: "move-to",
      title: "Move Player",
      description:
        "Walks the player character to the given position at normal speed (same as click-to-walk), resolving once they arrive. Approaching a portal or interactive item along the way may trigger it automatically, which can end the walk early. Coordinates are clamped to the current scene's bounds. Use this to move the player to interact with distant objects or explore the scene.",
      inputSchema: {
        type: "object",
        properties: {
          x: { type: "number", description: "X coordinate in scene pixels" },
          y: { type: "number", description: "Y coordinate in scene pixels" },
        },
        required: ["x", "y"],
      },
      outputSchema: toJsonSchema(MoveResultSchema),
      execute: async (args) => {
        const x = Number(args.x);
        const y = Number(args.y);
        if (Number.isNaN(x) || Number.isNaN(y)) {
          return {
            content: [
              { type: "text", text: "Error: x and y must be valid numbers" },
            ],
          };
        }
        const position = await bridge.moveTo({ x, y });
        return {
          content: [{ type: "text", text: JSON.stringify({ moved: true, position }) }],
        };
      },
      annotations: { destructiveHint: false, idempotentHint: false },
    },
    {
      name: "move-entity",
      title: "Move Scene Entity",
      description:
        "Drags a scene entity (furniture, containers, etc.) to a new position, same as a player drag. Use this to move draggable objects, e.g. to trigger a hidden reveal underneath.",
      inputSchema: {
        type: "object",
        properties: {
          entityId: { type: "string", description: "The entity to move (use get-scene to find draggable entities)" },
          x: { type: "number", description: "X coordinate in scene pixels" },
          y: { type: "number", description: "Y coordinate in scene pixels" },
        },
        required: ["entityId", "x", "y"],
      },
      outputSchema: toJsonSchema(MoveResultSchema),
      execute: (args) => {
        const id = args.entityId as string;
        const x = Number(args.x);
        const y = Number(args.y);
        if (!id)
          return {
            content: [{ type: "text", text: "Error: entityId is required" }],
          };
        if (Number.isNaN(x) || Number.isNaN(y)) {
          return {
            content: [
              { type: "text", text: "Error: x and y must be valid numbers" },
            ],
          };
        }
        return bridge.moveEntity(id, { x, y });
      },
      annotations: { destructiveHint: false, idempotentHint: false },
    },

    // ── Inventory management tools ──────────────────────────────────
    {
      name: "take",
      title: "Take Entity",
      description:
        "Picks up an entity from the scene and adds it to the player's inventory. The entity must be takeable (not a portal or decoration).",
      inputSchema: {
        type: "object",
        properties: {
          entityId: {
            type: "string",
            description:
              "The entity to pick up (use get-scene or get-nearby to find takeable entities)",
          },
        },
        required: ["entityId"],
      },
      outputSchema: toJsonSchema(TakeResultSchema),
      execute: (args) => {
        const id = args.entityId as string;
        if (!id)
          return {
            content: [{ type: "text", text: "Error: entityId is required" }],
          };
        return bridge.take(id);
      },
      annotations: { destructiveHint: true, idempotentHint: false },
    },
    {
      name: "drop",
      title: "Drop Inventory Item",
      description:
        "Drops an inventory item at the given position in the scene. Use this to place items from inventory into the world.",
      inputSchema: {
        type: "object",
        properties: {
          instanceId: {
            type: "string",
            description: "The inventory item instance id (from get-inventory)",
          },
          x: { type: "number", description: "X coordinate to drop at" },
          y: { type: "number", description: "Y coordinate to drop at" },
        },
        required: ["instanceId", "x", "y"],
      },
      outputSchema: toJsonSchema(DropResultSchema),
      execute: (args) => {
        const id = args.instanceId as string;
        const x = Number(args.x);
        const y = Number(args.y);
        if (!id)
          return {
            content: [{ type: "text", text: "Error: instanceId is required" }],
          };
        if (Number.isNaN(x) || Number.isNaN(y)) {
          return {
            content: [
              { type: "text", text: "Error: x and y must be valid numbers" },
            ],
          };
        }
        return bridge.drop(id, { x, y });
      },
      annotations: { destructiveHint: true, idempotentHint: false },
    },

    // ── Interaction tools ───────────────────────────────────────────
    {
      name: "interact",
      title: "Interact with Entity",
      description:
        "Performs an action on an entity. Available actions depend on the entity. Common actions: look (examine visually), inspect (read description), examine (closer look), rub (polish), open (open container). Always get-entity first to see available actions.",
      inputSchema: {
        type: "object",
        properties: {
          entityId: {
            type: "string",
            description: "The entity to interact with",
          },
          action: {
            type: "string",
            description:
              "The interaction id. Common: 'look', 'inspect', 'examine', 'rub', 'open'. Use get-entity to see available actions for this entity.",
          },
        },
        required: ["entityId", "action"],
      },
      outputSchema: toJsonSchema(InteractResultSchema),
      execute: (args) => {
        const id = args.entityId as string;
        const action = args.action as string;
        if (!id)
          return {
            content: [{ type: "text", text: "Error: entityId is required" }],
          };
        if (!action)
          return {
            content: [{ type: "text", text: "Error: action is required" }],
          };
        return bridge.interact(id, action);
      },
      annotations: { idempotentHint: false },
    },

    // ── Navigation tools ────────────────────────────────────────────
    {
      name: "navigate",
      title: "Navigate Through Portal",
      description:
        "Travels through a portal entity to another scene. The portal must have a 'portal' component. Use get-scene to find available portals and their destination scenes.",
      inputSchema: {
        type: "object",
        properties: {
          portalId: {
            type: "string",
            description:
              "The portal entity id (e.g. 'door-back' to bedroom, 'forest-gate' to forest). Use get-scene to see available portals.",
          },
        },
        required: ["portalId"],
      },
      outputSchema: toJsonSchema(NavigateResultSchema),
      execute: (args) => {
        const id = args.portalId as string;
        if (!id)
          return {
            content: [{ type: "text", text: "Error: portalId is required" }],
          };
        return bridge.navigate(id);
      },
      annotations: { destructiveHint: false, idempotentHint: false },
    },

    // ── Equipment tools ─────────────────────────────────────────────
    {
      name: "equip",
      title: "Equip Wearable Item",
      description:
        "Equips a wearable item from inventory onto the player character. Use get-inventory to see available items and their categories.",
      inputSchema: {
        type: "object",
        properties: {
          instanceId: {
            type: "string",
            description:
              "The inventory item instance id to equip (from get-inventory)",
          },
        },
        required: ["instanceId"],
      },
      outputSchema: toJsonSchema(EquipResultSchema),
      execute: (args) => {
        const id = args.instanceId as string;
        if (!id)
          return {
            content: [{ type: "text", text: "Error: instanceId is required" }],
          };
        return bridge.equip(id);
      },
      annotations: { destructiveHint: false, idempotentHint: false },
    },
    {
      name: "unequip",
      title: "Unequip Worn Item",
      description:
        "Removes a worn item from the player character and returns it to inventory.",
      inputSchema: {
        type: "object",
        properties: {
          slot: {
            type: "string",
            description:
              "The clothing slot to unequip (e.g. 'hat', 'accessory')",
          },
        },
        required: ["slot"],
      },
      outputSchema: toJsonSchema(UnequipResultSchema),
      execute: (args) => {
        const slot = args.slot as string;
        if (!slot)
          return {
            content: [{ type: "text", text: "Error: slot is required" }],
          };
        return bridge.unequip(slot);
      },
      annotations: { destructiveHint: true, idempotentHint: false },
    },

    // ── Discovery tools ─────────────────────────────────────────────
    {
      name: "discover",
      title: "Discover Artifact",
      description:
        "Reveals an artifact's true identity. Changes its definitionId and unlocks associated knowledge nodes. Use this when you find an unknown artifact.",
      inputSchema: {
        type: "object",
        properties: {
          entityId: {
            type: "string",
            description: "The artifact entity to discover",
          },
        },
        required: ["entityId"],
      },
      outputSchema: toJsonSchema(DiscoverResultSchema),
      execute: (args) => {
        const id = args.entityId as string;
        if (!id)
          return {
            content: [{ type: "text", text: "Error: entityId is required" }],
          };
        return bridge.discover(id);
      },
      annotations: { destructiveHint: false, idempotentHint: false },
    },
    {
      name: "examine",
      title: "Examine Artifact",
      description:
        "Examines an artifact, progressing knowledge to 'partiallyUnderstood'. May unlock new information. Use this to learn more about discovered artifacts.",
      inputSchema: {
        type: "object",
        properties: {
          entityId: {
            type: "string",
            description: "The artifact entity to examine",
          },
        },
        required: ["entityId"],
      },
      outputSchema: toJsonSchema(ExamineResultSchema),
      execute: (args) => {
        const id = args.entityId as string;
        if (!id)
          return {
            content: [{ type: "text", text: "Error: entityId is required" }],
          };
        return bridge.examine(id);
      },
      annotations: { destructiveHint: false, idempotentHint: false },
    },
    {
      name: "restart-game",
      title: "Restart Game",
      description:
        "Resets the game to the initial state. Use this to restart the game from the beginning.",
      inputSchema: { type: "object", properties: {} },
      outputSchema: toJsonSchema(RestartResultSchema),
      execute: () => bridge.restartGame(),
      annotations: { destructiveHint: true, idempotentHint: false },
    },
  ];
}
